import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';
import { ProjectQuery } from 'src/app/projects/state/project.query';
import { ProjectService } from 'src/app/projects/state/project.service';
import { GeospatialService } from '../../shared/geospatial.service';
import { GeometricFormService } from '../../state/geometric-form.service';
import { GeometricFormType } from '../../state/geometric-form.type';
import { Vertex, VertexClass } from '../../vertexes/state/vertex.model';
import { createEdge, Edge, EdgeClass } from './edge.model';
import { EdgeStore } from './edge.store';

@Injectable({ providedIn: 'root' })
export class EdgeService extends GeometricFormService<Edge>{

  constructor(
    private edgeStore: EdgeStore,
    private projectQuery: ProjectQuery,
    private projectService: ProjectService,
    private http: HttpClient,
    private geospatialService: GeospatialService,
  ) {
    super(edgeStore);
  }

  get() {
    this.edgeStore.setLoading(true);
    return this.http.get<Edge[]>(`/api/project/${this.projectQuery.getActiveId()}/edge/`)
      .pipe(tap(entities => {
        this.edgeStore.set(entities);
      }),
        finalize(() => this.edgeStore.setLoading(false))
      );
  }

  add(edge: Edge): Observable<Edge> {
    return this.http.post<Edge>(`/api/project/${this.projectQuery.getActiveId()}/edge/`, edge)
      .pipe(tap(e => {
        this.edgeStore.add(e);

        const project = this.projectQuery.getActive();
        if (!project.validated) {
          this.projectService.validate(this.projectQuery.getActiveId())
            .pipe(take(1))
            .subscribe();
        }
      }));
  }

  create(vertexA: Vertex, vertexB: Vertex): Observable<Edge> {
    const edgeClass = this.getEdgeClasse(vertexA, vertexB);

    const edge = createEdge({
      vertex_a: vertexA.id,
      vertex_b: vertexB.id,
      va_latitude: vertexA.latitude,
      va_longitude: vertexA.longitude,
      vb_latitude: vertexB.latitude,
      vb_longitude: vertexB.longitude,
      edge_class: edgeClass
    });

    const distance = this.geospatialService.meterDistance(edge);
    edge.geom_distance = distance;
    edge.user_distance = distance;

    return this.add(edge);
  }

  update(id: number, edge: Partial<Edge>) {
    return this.http.put(`/api/project/${this.projectQuery.getActiveId()}/edge/${id}`, edge)
      .pipe(tap(v => {
        this.edgeStore.update(id, v);
      }));
  }

  remove(id: number) {
    return this.http.delete(`/api/project/${this.projectQuery.getActiveId()}/edge/${id}`)
      .pipe(tap(v => {
        this.edgeStore.remove(id);

        const project = this.projectQuery.getActive();
        if (project.validated) {
          this.projectService.validate(this.projectQuery.getActiveId())
            .pipe(take(1))
            .subscribe();
        }
      }));
  }

  getFull(id: number) {
    this.edgeStore.setLoading(true);
    return this.http.get<Edge>(`/api/project/${this.projectQuery.getActiveId()}/edge/${id}`)
      .pipe(tap(v => {
        this.edgeStore.update(v.id, v);
      }),
        finalize(() => this.edgeStore.setLoading(false))
      );
  }

  getClasses(): { id: EdgeClass, name: string }[] {
    return [
      { id: EdgeClass.A, name: 'Aerial' },
      { id: EdgeClass.U, name: 'Underground' },
      { id: EdgeClass.L, name: 'Lateral' }
    ];
  }

  getType(): GeometricFormType {
    return 'mitra-edge';
  }

  filter(legends: string[]) {

    if (!legends.length) {
      this.edgeStore.update(null, { hidder: false });
      return;
    }

    this.edgeStore.update(null, ({ map_legend }) => {
      return {
        hidder: legends.indexOf(map_legend) < 0
      };
    });
  }

  updateVertex(vertexId: number, latitude: number, longitude: number): void {
    this.edgeStore.update(
      ({ vertex_a }) => vertex_a === vertexId,
      { va_latitude: latitude, va_longitude: longitude }
    );

    this.edgeStore.update(
      ({ vertex_b }) => vertex_b === vertexId,
      { vb_latitude: latitude, vb_longitude: longitude }
    );
  }

  getEdgeClasse(vertexA: Vertex, vertexB: Vertex): EdgeClass {

    if (
      this.isType(vertexA, VertexClass.UB) &&
      this.isType(vertexB, VertexClass.UB)
    ) {
      return EdgeClass.U;
    }

    if (
      this.isType(vertexA, VertexClass.UB) && this.isType(vertexB, VertexClass.PL) ||
      this.isType(vertexA, VertexClass.PL) && this.isType(vertexB, VertexClass.UB)
    ) {
      return EdgeClass.L;
    }

    if (
      this.isType(vertexA, VertexClass.UB) && this.isType(vertexB, VertexClass.CO) ||
      this.isType(vertexA, VertexClass.CO) && this.isType(vertexB, VertexClass.UB)
    ) {
      return EdgeClass.U;
    }

    if (
      this.isType(vertexA, VertexClass.AC) && this.isType(vertexB, VertexClass.PL) ||
      this.isType(vertexA, VertexClass.PL) && this.isType(vertexB, VertexClass.AC)
    ) {
      return EdgeClass.A;
    }

    if (
      this.isType(vertexA, VertexClass.PL) &&
      this.isType(vertexB, VertexClass.PL)
    ) {
      return EdgeClass.A;
    }

    if (
      this.isType(vertexA, VertexClass.AC) &&
      this.isType(vertexB, VertexClass.AC)
    ) {
      return EdgeClass.A;

    }
    return null;
  }

  private isType(vertex: Vertex, vertexClass: VertexClass) {
    return vertex.vertex_class === vertexClass;
  }

}
